import React, { useMemo } from 'react';
import OrderSidebarStyleCard from './OrderSidebarStyleCard';
import { groupItemsForSidebarDisplay, resolveSidebarOrderCountBadge } from '../utils/orderSidebarDisplay';

/**
 * Strategy sidebar / Black Ops operatives list — grouped rows with OrderSidebarStyleCard.
 */
export default function OrderSidebarOrderList({
  items,
  getGroupKey,
  getGroupCount,
  resolveOrderRecord = (item) => item,
  getDisplayName = (item) => item?.name ?? '',
  getDisplayCost = (item) => item?.cost,
  costLabel = 'Cost',
  isItemUnowned,
  isItemDisabled,
  onItemClick,
  onItemMouseEnter,
  onItemMouseLeave,
  renderRowWrapperClassName,
  renderAfterCard,
  getItemTitle,
  listClassName = '',
}) {
  const groups = useMemo(
    () => groupItemsForSidebarDisplay(items, getGroupKey),
    [items, getGroupKey]
  );

  return (
    <div className={`strategy-sidebar-order-list${listClassName ? ` ${listClassName}` : ''}`.trim()}>
      {groups.map((group) => {
        const item = group.item;
        const order = resolveOrderRecord(item);
        const rawCount = getGroupCount ? getGroupCount(group) : group.count;
        const count = resolveSidebarOrderCountBadge(rawCount, order);
        const unowned = isItemUnowned?.(item, group) === true;
        const disabled = isItemDisabled?.(item, group) === true;
        const rowClass =
          typeof renderRowWrapperClassName === 'function'
            ? renderRowWrapperClassName(item, group)
            : renderRowWrapperClassName || '';

        const hasRowWrapper = Boolean(renderAfterCard || rowClass);
        const rowMouseHandlers = {
          onMouseEnter: onItemMouseEnter ? (e) => onItemMouseEnter(item, group, e) : undefined,
          onMouseLeave: onItemMouseLeave ? () => onItemMouseLeave(item, group) : undefined,
        };

        const card = (
          <OrderSidebarStyleCard
            order={order}
            displayName={getDisplayName(item)}
            displayCost={getDisplayCost(item)}
            count={count}
            costLabel={costLabel}
            isUnowned={unowned}
            onClick={
              onItemClick
                ? (e) => {
                    e.stopPropagation();
                    if (!disabled) onItemClick(item, group, e);
                  }
                : undefined
            }
            onMouseEnter={hasRowWrapper ? undefined : rowMouseHandlers.onMouseEnter}
            onMouseLeave={hasRowWrapper ? undefined : rowMouseHandlers.onMouseLeave}
            style={{
              cursor: disabled ? 'not-allowed' : onItemClick ? 'pointer' : 'default',
            }}
            title={getItemTitle ? getItemTitle(item, group) : undefined}
          />
        );

        if (!hasRowWrapper) {
          return <React.Fragment key={group.key}>{card}</React.Fragment>;
        }

        return (
          <div
            key={group.key}
            className={`strategy-sidebar-list-row${rowClass ? ` ${rowClass}` : ''}`.trim()}
            {...rowMouseHandlers}
          >
            {card}
            {renderAfterCard?.(item, group)}
          </div>
        );
      })}
    </div>
  );
}
